// 2026-08-23 scale a11y — WCAG 2.2, keyboard nav, skip link, focus-visible
"use client";

import { Component, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/use-i18n";
import {
  CHUNK_RELOAD_SESSION_KEY,
  isChunkLoadFailure,
  shouldSuppressClientErrorReport,
} from "@/lib/browser-console-noise";
import { reportClientError } from "@/lib/client-error-reporting";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

/** Cooldown so a bad deploy cannot infinite-loop reload. */
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;
const CHUNK_RELOAD_ATTEMPTS_KEY = "vortixy-chunk-reload-attempts";
const MAX_CHUNK_RELOAD_ATTEMPTS = 3;

function ErrorFallback({ onRetry }: { onRetry: () => void }) {
  const t = useT();
  return (
    <div className="flex min-h-[200px] items-center justify-center p-8" role="alert">
      <div className="text-center">
        <p className="text-lg font-medium text-foreground">{t("errorTitle")}</p>
        <p className="mt-2 text-sm text-muted-foreground">{t("errorBody")}</p>
        <Button variant="accent" size="default" className="mt-4" onClick={onRetry}>
          {t("errorRetry")}
        </Button>
      </div>
    </div>
  );
}

function tryChunkAutoReload(error: Error): boolean {
  if (typeof window === "undefined") return false;
  const msg = error.message ?? "";
  const name = error.name ?? "";
  if (!isChunkLoadFailure(msg) && !isChunkLoadFailure(name)) {
    return false;
  }
  try {
    const prev = Number(sessionStorage.getItem(CHUNK_RELOAD_SESSION_KEY) || "0");
    const attempts = Number(sessionStorage.getItem(CHUNK_RELOAD_ATTEMPTS_KEY) || "0");
    // Exponential cooldown: 0s → 2s → 5s between reload attempts.
    const cooldownMs = attempts === 0 ? 0 : attempts === 1 ? 2_000 : 5_000;
    if (
      Number.isFinite(prev) &&
      prev > 0 &&
      Date.now() - prev < Math.max(CHUNK_RELOAD_COOLDOWN_MS, cooldownMs)
    ) {
      // Already reloaded recently — surface fallback UI instead of looping.
      return false;
    }
    if (attempts >= MAX_CHUNK_RELOAD_ATTEMPTS) {
      // Max retries exhausted — show fallback UI with manual retry.
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_SESSION_KEY, String(Date.now()));
    sessionStorage.setItem(CHUNK_RELOAD_ATTEMPTS_KEY, String(attempts + 1));
  } catch {
    // sessionStorage blocked — still attempt one reload; without persistence we
    // cannot guard against a loop, but most browsers allow sessionStorage.
  }
  // Hard reload picks up the new deploy manifest; soft reset cannot.
  window.location.reload();
  return true;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Post-deploy chunk miss: one automatic full reload (cooldown-guarded).
    if (tryChunkAutoReload(error)) {
      return;
    }

    console.error("[ErrorBoundary]", error, errorInfo);

    const ua = typeof navigator !== "undefined" ? navigator.userAgent : undefined;
    if (shouldSuppressClientErrorReport(error.message, ua)) {
      return;
    }

    reportClientError({
      message: error.message,
      stack: error.stack,
      digest: "digest" in error ? String(error.digest) : undefined,
      locationHref: window.location.href,
      pathname: window.location.pathname,
      userAgent: ua,
    });
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <ErrorFallback
          onRetry={() => {
            const msg = this.state.error?.message ?? "";
            const name = this.state.error?.name ?? "";
            // Prefer full reload for chunk/runtime mismatches; soft reset otherwise.
            if (isChunkLoadFailure(msg) || isChunkLoadFailure(name)) {
              window.location.reload();
              return;
            }
            this.setState({ hasError: false, error: undefined });
          }}
        />
      );
    }
    return this.props.children;
  }
}
